import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import type { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { type FC, useState } from "react";
import { usePerformGlobalRefresh } from "../../../../components/header/hooks";
import { InHouseTooltip } from "../../../../components/InHouseTooltip/InHouseTooltip";
import { useApproveTimeOffRequest, useDeclineTimeOffRequest } from "../../../../components/RequestTimeOff/timeOffRequestsHooks";
import { VirtualizedList } from "../../../../components/UIToolkit/molecules/VirtualizedList";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import { MultiSizePopup } from "../../../../components/UIToolkit/Popups/MultiSizePopup/MultiSizePopup";
import { MultiSizePopupState } from "../../../../components/UIToolkit/Popups/MultiSizePopup/utils";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { formatWeekRangeWithMonth } from "../../../../utils/dateFormatting";
import { type TimeOffRequest, TimeOffRequestReason, type TimeOffRequestStatus } from "../../../../utils/models";

type Props = {
	dialogOpen: boolean;
	handleClose: () => void;
	timeOffRequestsOfSpecificStaff: TimeOffRequest[];
	refetchRequests?: (() => Promise<void>) | ((options?: RefetchOptions) => Promise<QueryObserverResult<TimeOffRequest[], Error>>);
	refetchDayOffRequestsLoading?: boolean;
};

export const getStatusColor = (status: TimeOffRequestStatus) => {
	switch (status) {
		case "pending":
			return "#FFA726";
		case "approved":
			return "#66BB6A";
		case "declined":
			return "#EF5350";
		default:
			return "#757575";
	}
};

export const TimeOffRequestDialog: FC<Props> = ({ dialogOpen, handleClose, timeOffRequestsOfSpecificStaff, refetchRequests, refetchDayOffRequestsLoading }) => {
	const { getNurseFormattedName, getNurseById } = useHospitalData();
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [confirmAction, setConfirmAction] = useState<"approve" | "decline" | null>(null);
	const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
	const { mutateAsync: approveRequest, isPending: isApproving } = useApproveTimeOffRequest();
	const { mutateAsync: declineRequest, isPending: isDeclining } = useDeclineTimeOffRequest();
	const { performGlobalRefresh, isLoading: isLoadingGlobalRefresh } = usePerformGlobalRefresh({});

	const sortedNurseRequests = timeOffRequestsOfSpecificStaff.sort((a, b) => b.startDate.diff(a.startDate, "days").days);

	const handleApproveClick = (requestId: string) => {
		setSelectedRequestId(requestId);
		setConfirmAction("approve");
		setConfirmDialogOpen(true);
	};

	const handleDeclineClick = (requestId: string) => {
		setSelectedRequestId(requestId);
		setConfirmAction("decline");
		setConfirmDialogOpen(true);
	};

	const handleConfirmAction = async () => {
		if (!selectedRequestId || !confirmAction) return;

		try {
			if (confirmAction === "approve") {
				await approveRequest(selectedRequestId);
			} else {
				await declineRequest(selectedRequestId);
			}
			await performGlobalRefresh({
				avoidRefreshFor: {
					requestsData: true,
					notificationsData: true,
				},
			});

			setConfirmDialogOpen(false);
			setSelectedRequestId(null);
			setConfirmAction(null);
		} catch {
			// Error handled in hook
		}
	};

	const handleCancelConfirm = () => {
		setConfirmDialogOpen(false);
		setSelectedRequestId(null);
		setConfirmAction(null);
	};

	const refreshResults = async () => {
		if (refetchRequests) {
			await refetchRequests();
		}
	};

	const nurse = getNurseById(timeOffRequestsOfSpecificStaff.at(0)?.requestingStaffId ?? 0);
	const nurseName = nurse ? getNurseFormattedName(nurse.id) : "";

	return (
		<>
			<MultiSizePopup
				title="Time Off Requests"
				open={dialogOpen}
				onClose={handleClose}
				disableMaximize={true}
				bodyRenderer={(currentSizeState) => {
					if (currentSizeState === MultiSizePopupState.HIDDEN) {
						return null;
					}

					return (
						<Box sx={{ p: 2, minHeight: "200px", maxHeight: "500px" }}>
							<Box
								sx={{
									display: "flex",
									gap: 1,
									alignItems: "center",
								}}
							>
								<Typography>Showing time off requests for</Typography>
								<Tooltip title={nurseName} disableHoverListener={!nurse}>
									<Typography sx={{ fontWeight: 600 }}>{nurseName}</Typography>
								</Tooltip>
								<Tooltip title={"Refresh"}>
									<IconButton size={"small"} disabled={refetchDayOffRequestsLoading}>
										<RefreshOutlined fontSize={"inherit"} onClick={refreshResults} />
									</IconButton>
								</Tooltip>
							</Box>
							{refetchDayOffRequestsLoading ? (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										minHeight: "100px",
									}}
								>
									<CircularProgress />
								</Box>
							) : sortedNurseRequests.length === 0 ? (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										minHeight: "100px",
									}}
								>
									<Typography color="textSecondary">No time off requests found</Typography>
								</Box>
							) : (
								<VirtualizedList
									items={sortedNurseRequests}
									rowHeight={80}
									renderItem={(request) => {
										const isPending = request.status === "pending";
										const isProcessing = (isApproving || isDeclining || isLoadingGlobalRefresh) && selectedRequestId === request.id;

										return (
											<Box
												key={request.id}
												sx={{
													display: "flex",
													flexDirection: "column",
													gap: 1,
													p: 2,
													borderBottom: "1px solid #E0E0E0",
													"&:last-child": {
														borderBottom: "none",
													},
												}}
											>
												<Box
													sx={{
														display: "flex",
														justifyContent: "space-between",
														alignItems: "center",
													}}
												>
													<Box>
														<Typography
															variant="body2"
															sx={{
																fontWeight: 600,
															}}
														>
															{formatWeekRangeWithMonth(request.startDate, request.endDate, true, true)}
														</Typography>
														<Typography variant="caption" color="textSecondary">
															{request.reason !== TimeOffRequestReason.Other ? request.reason : request.reasonExplanation}
														</Typography>
													</Box>
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															gap: 1,
														}}
													>
														<Typography
															variant="caption"
															sx={{
																color: getStatusColor(request.status),
																fontWeight: 600,
																textTransform: "capitalize",
															}}
														>
															{request.status}
														</Typography>
														{isPending && !isProcessing && (
															<>
																<InHouseTooltip title="Approve">
																	<IconButton size="small" onClick={() => handleApproveClick(request.id)} sx={{ color: "#66BB6A" }}>
																		<CheckCircleOutlineIcon fontSize="small" />
																	</IconButton>
																</InHouseTooltip>
																<InHouseTooltip title="Decline">
																	<IconButton size="small" onClick={() => handleDeclineClick(request.id)} sx={{ color: "#EF5350" }}>
																		<HighlightOffIcon fontSize="small" />
																	</IconButton>
																</InHouseTooltip>
															</>
														)}
														{isProcessing && <CircularProgress size={20} />}
													</Box>
												</Box>
											</Box>
										);
									}}
								/>
							)}
						</Box>
					);
				}}
			/>
			<SystemDialog
				open={confirmDialogOpen}
				onClose={handleCancelConfirm}
				title={confirmAction === "approve" ? "Approve Time Off Request" : "Decline Time Off Request"}
				body={`Are you sure you want to ${confirmAction} this time off request?`}
				cancel="Cancel"
				confirm={confirmAction === "approve" ? "Approve" : "Decline"}
				onConfirm={handleConfirmAction}
				confirmLoading={isApproving || isDeclining}
			/>
		</>
	);
};
